import { memo, useCallback, useMemo } from 'react';

import { isKnownClipSource, SOURCE_MAP } from '@air/utils-clips';

import { SelectChipOptionContainer } from '~/components/Select/components/SelectChipOptionContainer';
import { Select, type SelectProps } from '~/components/Select/Select';
import { type DefaultChipType } from '~/components/Select/shared/types';

type SourcesSelectProps = {
  selectedValues: string[];
  onSelectionChange: (newString: string[]) => void;
  sources: string[];
} & Omit<SelectProps, 'placeholder' | 'options' | 'selectedOptions' | 'onSelectionChange'>;

const sourceToOption = (source: string): DefaultChipType => ({
  value: source,
  label: isKnownClipSource(source) ? SOURCE_MAP[source] : source,
  borderRadius: 4,
});

export const SourcesSelect = memo(({ sources, selectedValues, onSelectionChange, ...props }: SourcesSelectProps) => {
  const options = useMemo<DefaultChipType[]>(() => sources.map((s) => sourceToOption(s)), [sources]);
  const selectedOptions = useMemo<DefaultChipType[]>(
    () => selectedValues.map((s) => sourceToOption(s)),
    [selectedValues],
  );

  const onValuesChange: SelectProps<DefaultChipType>['onSelectionChange'] = useCallback(
    ({ chips }) => {
      onSelectionChange(chips.map((chip) => chip.value));
    },
    [onSelectionChange],
  );

  const listItemRenderer = useCallback<Required<SelectProps>['listItemRenderer']>(
    (option) => (
      <SelectChipOptionContainer className="flex justify-between py-2">
        <p className="ads:text-primary text-14 text-gray-12">{option.label}</p>
      </SelectChipOptionContainer>
    ),
    [],
  );

  return (
    <Select
      data-testid="SOURCES_SELECT"
      placeholder="Search upload sources"
      listItemRenderer={listItemRenderer}
      maxDropdownHeight={300}
      options={options}
      renderAsInput
      isSearchable
      onSelectionChange={onValuesChange}
      selectedOptions={selectedOptions}
      alwaysShowClearItemButton
      isClearable
      isSticky
      size="large"
      {...props}
    />
  );
});

SourcesSelect.displayName = 'SourcesSelect';
